import { DialogM } from "@common/components/alert/dialog/Dialog"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { useDisconnectFromLive } from "../../engine/live/room/useDisconnectFromLive"
import { IS_WEB } from "@common/utils/mobileUtils"
import { useInterval } from "@common/utils/useInterval"
import {
    pauseActivity,
    updateActivityEvent,
} from "@data/redux/actions/activity"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import {
    changeUnreadMessageInLiveCount,
    newMessage,
} from "@data/redux/actions/broadcast"
import { setLiveKitToken } from "@data/redux/actions/liveKitRoom"
import {
    addInCallUser,
    addParticipant,
    addRaisedHand,
    clearMembers,
    hideIncomingCall,
    newLiveSessionLog,
    removeInCallUsers,
    removeParticipant,
    removePhoneRinging,
    removeRaisedHand,
    batchUpdateTimers,
    setActiveTime,
    setActivityFinalizedCount,
    setCallPortal,
    setInCallUsers,
    setIsLiveModeEnabled,
    setIssoundMode,
    setIsTyping,
    setLiveParticipants,
    setRaisedHands,
    setTimesSinceLastCall,
    setTypingUser,
    setVoiceRecordingIds,
    showIncomingCall,
} from "@data/redux/actions/liveMode"
import { setMuteAudio } from "@data/redux/actions/videoCall"
import { liveServiceV2 } from "@data/services/liveServiceV2"
import { useRoomContext } from "@livekit/components-react"
import { useKeepAwake } from "expo-keep-awake"
import React, { useCallback, useEffect, useRef } from "react"
import { connect } from "react-redux"
import { hasRole } from "../../data/utility/ability"
import LiveContainerV3 from "./liveV3/LiveContainerV3"
import { CONNECT_OPTIONS } from "../../engine/live/room/roomOptions"
import { reportLiveRoomFailure } from "./reportLiveRoomFailure"
import { useTutorMediaAccessGate } from "./useTutorMediaAccessGate"

const LiveMainLayout = (props) => {
    if (!IS_WEB) useKeepAwake("Live", { suppressDeactivateWarnings: true })

    const room = useRoomContext()
    const { disconnectFromLive } = useDisconnectFromLive()

    // Track participants who have connected for the first time today
    const previousParticipantsList = useRef(new Set())

    // Update metrics timer - collect all updates and dispatch a single batched action
    useInterval(
        () => {
            // Skip interval logic when setSoundMode is processing to avoid race conditions
            if (props.isSoundModeProcessing) {
                return
            }

            const timesSinceLastCallUpdates = []
            const activeTimeUpdates = []

            // Collect timesSinceLastCall updates - only for participants online and NOT in sound mode
            if (props.timesSinceLastCall) {
                Object.keys(props.timesSinceLastCall).forEach((userId) => {
                    const userIdInt = parseInt(userId)
                    const isInSoundMode = props.soundModeIds?.includes(userIdInt)
                    const isOnline = props.participants?.includes(userIdInt)

                    if (isOnline && !isInSoundMode) {
                        timesSinceLastCallUpdates.push({
                            participantId: userIdInt,
                            timesSinceLastCall: props.timesSinceLastCall[userId],
                        })
                    }
                })
            }

            // Collect activeTime updates - only for participants in sound mode
            if (props.activeTime) {
                props.activeTime.forEach((userActiveTime) => {
                    const isInSoundMode = props.soundModeIds?.includes(userActiveTime.id)
                    const isOnline = props.participants?.includes(userActiveTime.id)

                    if (isInSoundMode && isOnline) {
                        activeTimeUpdates.push({
                            participantId: userActiveTime.id,
                            activeTime: userActiveTime.time,
                        })
                    }
                })
            }

            // Single dispatch for all timer updates
            if (timesSinceLastCallUpdates.length > 0 || activeTimeUpdates.length > 0) {
                props.batchUpdateTimers(
                    timesSinceLastCallUpdates,
                    activeTimeUpdates,
                    props.timesSinceLastCallCalculatedAt || props.activeTimeCalculatedAt
                )
            }
        },
        hasRole(props.user, "tutor") ? 5 * 1000 : null // Update every 5 seconds for tutors only
    )

    const getLiveSessionService = liveServiceV2.getLiveSessionV2
    const getSessionData = () => {
        props.apiRequest(getLiveSessionService, { id: props.liveSessionId })
    }

    const getMetricsService = liveServiceV2.getMetricsV2
    const getMetricsData = (noCache = false) => {
        if (hasRole(props.user, "tutor") && props.liveSessionId) {
            const routeParams = { id: props.liveSessionId }
            const queryParams = noCache ? { no_cache: "1" } : {}
            props.apiRequest(getMetricsService, routeParams, queryParams)
        }
    }

    // Every 60 seconds, get live session
    useInterval(() => {
        getSessionData()
    }, 60 * 1000)

    useEffect(() => {
        getSessionData()
        if (hasRole(props.user, "tutor")) {
            getMetricsData(true) // no_cache = true for first-time join
        }
    }, [])

    // Get metrics when participants change
    useEffect(() => {
        if (hasRole(props.user, "tutor") && props.participants?.length > 0) {
            // Check if any participant is connecting for the first time today
            let hasNewParticipant = false

            props.participants.forEach((participantId) => {
                if (!previousParticipantsList.current.has(participantId)) {
                    hasNewParticipant = true
                }
            })

            // Call with no_cache if there's a new first-time participant
            if (hasNewParticipant) {
                getMetricsData(true) // no_cache = true for first-time participants
            } else {
                getMetricsData() // regular call for returning participants
            }

            previousParticipantsList.current = new Set(props.participants)
        }
    }, [props.participants])

    const applySession = useCallback(
        (successData) => {
            if (
                !props.roomData?.token ||
                room?.roomInfo?.name !==
                    successData?.live_session?.conference_link ||
                props.roomData?.roomName !==
                    successData?.live_session?.conference_link
            ) {
                props.setLiveKitToken(
                    successData.livekit_token,
                    successData?.live_session?.conference_link
                )

                if (
                    room?.roomInfo?.name !==
                    successData?.live_session?.conference_link
                ) {
                    room.disconnect(true).then(() => {
                        // 3 arguments : `Room.connect(url, token, opts)`. Le
                        // 4ᵉ (ROOM_OPTIONS) était ignoré depuis toujours — les
                        // options de room se passent au constructeur.
                        room.connect(
                            props.roomData?.serverUrl,
                            successData.livekit_token,
                            CONNECT_OPTIONS
                        )
                            .then()
                            .catch((reason) => {
                                console.log("CONNECTION ERROR LIVEKIT", reason)
                            })
                    })
                }
            }
        },
        [props.roomData]
    )

    // Le tuteur au natif : le jeton, qui connecte la salle et la fait publier, n'est posé qu'une fois
    // ses questions de permissions répondues (`useTutorMediaAccessGate`).
    const successCallback = useTutorMediaAccessGate(
        !IS_WEB && hasRole(props.user, "tutor"),
        applySession
    )

    useApiStatus(
        getLiveSessionService, // api service
        null, // success message on toast (string or null)
        true, // error message on toast (true) or keep it in redux state (false)
        successCallback,
        (error) => {
            disconnectFromLive()
            setTimeout(() => {
                console.log("getLiveSession error: ", error)
                reportLiveRoomFailure(error)
                if (
                    (error?.message ?? error?.error)?.startsWith(
                        "No query results for model"
                    )
                ) {
                    DialogM.show({
                        champion: "abou",
                        variant: "main",
                        text1: "Cette session n'est plus disponible",
                        text2: "Tu as été déconnecté du live",
                    })
                }
            }, 2000)
        }
    )

    return <LiveContainerV3 />
}

const mapStateToProps = (state) => {
    return {
        getLiveSessionRequest: state.api.getLiveSession?.request,
        getCallRequest: state.api.getCall?.request,
        user: state.data.currentUser?.object,
        successes: state.data.staticData?.successes,
        currentLiveSession: state.data.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === state.liveMode?.liveSessionId
        ),
        liveSessions: state.data.currentUser?.object?.live_sessions,
        liveSessionId: state.liveMode?.liveSessionId,
        tutors: state.liveMode?.liveTutors,
        participants: state.liveMode?.liveParticipants,
        inCallUsers: state.liveMode?.inCallUsers,
        todayLiveSessionLogs: state.liveMode?.todayLiveSessionLogs,
        timesSinceLastCall: state.liveMode?.timesSinceLastCall,
        timesSinceLastCallCalculatedAt:
            state.liveMode?.timesSinceLastCallCalculatedAt,
        activeTime: state.liveMode?.activeTime,
        activeTimeCalculatedAt: state.liveMode?.activeTimeCalculatedAt,
        callPortal: state.liveMode?.callPortal,
        conferenceToken: state.liveMode?.conferenceToken,
        screenshareToken: state.liveMode?.screenshareToken,
        visibleInvite: state.liveMode?.visibleInvite,
        soundMode: state.liveMode?.soundMode,
        soundModeIds: state.liveMode?.soundModeIds,
        hasPhonePermission: state.liveMode?.hasPhonePermission,
        ongoingActivity: state.activity?.ongoingActivity,
        cameraOn: state.activity?.cameraOn,
        roomData: state.liveKitRoom?.roomData,
        isSoundModeProcessing: state.liveMode?.isSoundModeProcessing,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    setIsLiveModeEnabled,
    setLiveParticipants,
    addParticipant,
    setIsTyping,
    setTypingUser,
    removeParticipant,
    clearMembers,
    setInCallUsers,
    addInCallUser,
    removeInCallUsers,
    setRaisedHands,
    addRaisedHand,
    removeRaisedHand,
    setCallPortal,
    showIncomingCall,
    hideIncomingCall,
    newMessage,
    newLiveSessionLog,
    removePhoneRinging,
    changeUnreadMessageInLiveCount,
    setTimesSinceLastCall,
    setActiveTime,
    batchUpdateTimers,
    setIssoundMode,
    setVoiceRecordingIds,
    setMuteAudio,
    setActivityFinalizedCount,
    pauseActivity,
    updateActivityEvent,
    setLiveKitToken,
}

export default connect(mapStateToProps, mapDispatchToProps)(LiveMainLayout)
